import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  Plus, 
  BarChart3, 
  Users, 
  Calendar,
  CheckCircle,
  Clock,
  Trash2,
  Edit,
  Eye,
  Send,
  X,
  ChevronDown,
  ChevronUp,
  Building,
  Star,
  MessageSquare,
  PieChart,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { id: 'single', label: 'Choix unique', icon: '○' },
  { id: 'multiple', label: 'Choix multiple', icon: '☐' },
  { id: 'rating', label: 'Notation (1-5)', icon: '★' },
  { id: 'text', label: 'Texte libre', icon: '✎' }
];

const Sondages = () => {
  const { token, user } = useAuth();
  const { markSurveyAsRead, refreshNotifications } = useNotifications();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyResults, setSurveyResults] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [filter, setFilter] = useState('all'); // all, active, closed, my

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    building_id: '',
    questions: [],
    deadline: '',
    anonymous: false,
    target_roles: ['coproprietaire', 'gestionnaire']
  });

  // Response state
  const [responses, setResponses] = useState({});

  const isAdmin = ['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet'].includes(user?.role);
  const isDirecteur = user?.role === 'directeur_cabinet';
  const canParticipate = !isDirecteur; // Directeurs ne participent pas aux sondages

  const fetchSurveys = useCallback(async () => {
    try {
      let url = `${API_URL}/api/surveys?limit=50`;
      if (filter === 'active') url += '&status=active';
      if (filter === 'closed') url += '&status=closed';
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        let filteredSurveys = data.surveys;
        if (filter === 'my') {
          filteredSurveys = filteredSurveys.filter(s => s.has_responded);
        }
        setSurveys(filteredSurveys);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  const fetchBuildings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/buildings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBuildings(data.buildings || data || []);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchSurveys();
    if (isAdmin) fetchBuildings();
    // Refresh notifications when visiting surveys page
    refreshNotifications();
  }, [fetchSurveys, fetchBuildings, isAdmin, refreshNotifications]);

  const handleCreateSurvey = async () => {
    if (!formData.title || formData.questions.length === 0) {
      alert('Veuillez remplir le titre et ajouter au moins une question');
      return;
    }

    try {
      // If selectedSurvey exists, we're updating
      const isUpdate = selectedSurvey && selectedSurvey.id;
      const url = isUpdate 
        ? `${API_URL}/api/surveys/${selectedSurvey.id}`
        : `${API_URL}/api/surveys`;
      const method = isUpdate ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setShowCreateModal(false);
        setSelectedSurvey(null);
        resetForm();
        fetchSurveys();
      } else {
        const error = await res.json();
        alert(error.detail || (isUpdate ? 'Erreur lors de la modification' : 'Erreur lors de la création'));
      }
    } catch (error) {
      console.error('Error saving survey:', error);
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce sondage ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/surveys/${surveyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchSurveys();
      }
    } catch (error) {
      console.error('Error deleting survey:', error);
    }
  };

  const handleCloseSurvey = async (surveyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir clôturer ce sondage ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/surveys/${surveyId}/close`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchSurveys();
      }
    } catch (error) {
      console.error('Error closing survey:', error);
    }
  };

  const handleViewResults = async (survey) => {
    try {
      const res = await fetch(`${API_URL}/api/surveys/${survey.id}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSurveyResults(data);
        setSelectedSurvey(survey);
        setShowResultsModal(true);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const handleOpenRespond = (survey) => {
    setSelectedSurvey(survey);
    setResponses({});
    setShowRespondModal(true);
  };

  const handleSubmitResponse = async () => {
    // Validate required questions
    for (const question of selectedSurvey.questions) {
      if (question.required && !responses[question.id]) {
        alert(`Veuillez répondre à la question: ${question.text}`);
        return;
      }
    }

    try {
      const res = await fetch(`${API_URL}/api/surveys/${selectedSurvey.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ answers: responses })
      });

      if (res.ok) {
        setShowRespondModal(false);
        setSelectedSurvey(null);
        setResponses({});
        fetchSurveys();
        // Update notification badge
        markSurveyAsRead();
      } else {
        const error = await res.json();
        alert(error.detail || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Error submitting response:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      building_id: '',
      questions: [],
      deadline: '',
      anonymous: false,
      target_roles: ['coproprietaire', 'gestionnaire']
    });
  };

  const handleEditSurvey = (survey) => {
    setSelectedSurvey(survey);
    setFormData({
      title: survey.title || '',
      description: survey.description || '',
      building_id: survey.building_id || '',
      anonymous: survey.anonymous || false,
      deadline: survey.deadline ? survey.deadline.split('T')[0] : '',
      target_roles: survey.target_roles || ['coproprietaire', 'gestionnaire'],
      questions: survey.questions || []
    });
    setShowCreateModal(true);
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      text: '',
      type: 'single',
      options: [
        { id: `opt_${Date.now()}_1`, text: '' },
        { id: `opt_${Date.now()}_2`, text: '' }
      ],
      required: true
    };
    setFormData({ ...formData, questions: [...formData.questions, newQuestion] });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...formData.questions];
    updated[index] = { ...updated[index], [field]: value };
    
    // Reset options if changing to/from text or rating type
    if (field === 'type') {
      if (value === 'text' || value === 'rating') {
        updated[index].options = [];
      } else if (!updated[index].options || updated[index].options.length === 0) {
        updated[index].options = [
          { id: `opt_${Date.now()}_1`, text: '' },
          { id: `opt_${Date.now()}_2`, text: '' }
        ];
      }
    }
    
    setFormData({ ...formData, questions: updated });
  };

  const removeQuestion = (index) => {
    const updated = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updated });
  };

  const addOption = (questionIndex) => {
    const updated = [...formData.questions];
    updated[questionIndex].options.push({
      id: `opt_${Date.now()}`,
      text: ''
    });
    setFormData({ ...formData, questions: updated });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...formData.questions];
    updated[questionIndex].options[optionIndex].text = value;
    setFormData({ ...formData, questions: updated });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...formData.questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setFormData({ ...formData, questions: updated });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isExpired = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="sondages-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BarChart3 className="text-[#00D3A8]" />
            Sondages & Consultations
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Consultez et participez aux sondages de votre copropriété
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors"
            data-testid="create-survey-btn"
          >
            <Plus size={18} />
            Nouveau sondage
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'Tous' },
          { id: 'active', label: 'Actifs' },
          { id: 'closed', label: 'Terminés' },
          { id: 'my', label: 'Mes réponses' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              filter === f.id
                ? 'bg-[#00D3A8] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Surveys List */}
      {surveys.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Aucun sondage</h3>
          <p className="text-gray-500">
            {filter === 'my' ? "Vous n'avez pas encore participé à un sondage" : "Aucun sondage disponible pour le moment"}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {surveys.map((survey) => (
            <div
              key={survey.id}
              className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              {/* Status badge */}
              <div className="flex items-start justify-between mb-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  survey.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {survey.status === 'active' ? 'Actif' : 'Terminé'}
                </span>
                {survey.has_responded && (
                  <CheckCircle className="text-green-500" size={18} />
                )}
              </div>

              {/* Title & description */}
              <h3 className="font-semibold text-gray-800 mb-2">{survey.title}</h3>
              {survey.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{survey.description}</p>
              )}

              {/* Meta info */}
              <div className="space-y-2 mb-4 text-sm text-gray-500">
                {survey.building_name && (
                  <div className="flex items-center gap-2">
                    <Building size={14} />
                    <span>{survey.building_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users size={14} />
                  <span>{survey.responses_count} réponse{survey.responses_count !== 1 ? 's' : ''}</span>
                </div>
                {survey.deadline && (
                  <div className={`flex items-center gap-2 ${isExpired(survey.deadline) ? 'text-red-500' : ''}`}>
                    <Clock size={14} />
                    <span>
                      {isExpired(survey.deadline) ? 'Expiré le' : 'Jusqu\'au'} {formatDate(survey.deadline)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <MessageSquare size={14} />
                  <span>{survey.questions?.length || 0} question{(survey.questions?.length || 0) !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                {survey.status === 'active' && !survey.has_responded && !isExpired(survey.deadline) && canParticipate && (
                  <button
                    onClick={() => handleOpenRespond(survey)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] text-sm"
                  >
                    <Send size={14} />
                    Répondre
                  </button>
                )}
                {(isAdmin || survey.status === 'closed') && (
                  <button
                    onClick={() => handleViewResults(survey)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  >
                    <PieChart size={14} />
                    Résultats
                  </button>
                )}
                {isAdmin && survey.status === 'active' && (
                  <>
                    <button
                      onClick={() => handleEditSurvey(survey)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Modifier"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleCloseSurvey(survey.id)}
                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                      title="Clôturer"
                    >
                      <CheckCircle size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteSurvey(survey.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Survey Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Nouveau sondage</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  placeholder="Ex: Choix du prestataire pour l'entretien"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  rows={3}
                  placeholder="Décrivez l'objectif de ce sondage..."
                />
              </div>

              {/* Building */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Immeuble</label>
                  <select
                    value={formData.building_id}
                    onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  >
                    <option value="">Tous les immeubles</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.nom || b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date limite</label>
                  <input
                    type="datetime-local"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.anonymous}
                    onChange={(e) => setFormData({ ...formData, anonymous: e.target.checked })}
                    className="w-4 h-4 text-[#00D3A8] rounded focus:ring-[#00D3A8]"
                  />
                  <span className="text-sm text-gray-700">Réponses anonymes</span>
                </label>
              </div>

              {/* Questions */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">Questions</label>
                  <button
                    onClick={addQuestion}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-[#00D3A8] hover:bg-teal-50 rounded-lg"
                  >
                    <Plus size={16} />
                    Ajouter une question
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.questions.map((question, qIndex) => (
                    <div key={question.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start gap-3 mb-3">
                        <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm font-medium">
                          {qIndex + 1}
                        </span>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                            placeholder="Votre question..."
                          />
                        </div>
                        <button
                          onClick={() => removeQuestion(qIndex)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="flex items-center gap-4 mb-3 ml-9">
                        <select
                          value={question.type}
                          onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
                          className="px-3 py-1 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                        >
                          {QUESTION_TYPES.map((t) => (
                            <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => updateQuestion(qIndex, 'required', e.target.checked)}
                            className="w-4 h-4 text-[#00D3A8] rounded"
                          />
                          Obligatoire
                        </label>
                      </div>

                      {/* Options for single/multiple */}
                      {(question.type === 'single' || question.type === 'multiple') && (
                        <div className="ml-9 space-y-2">
                          {question.options?.map((option, oIndex) => (
                            <div key={option.id} className="flex items-center gap-2">
                              <span className="text-gray-400">
                                {question.type === 'single' ? '○' : '☐'}
                              </span>
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                className="flex-1 px-3 py-1 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                                placeholder={`Option ${oIndex + 1}`}
                              />
                              {question.options.length > 2 && (
                                <button
                                  onClick={() => removeOption(qIndex, oIndex)}
                                  className="p-1 text-gray-400 hover:text-red-500"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addOption(qIndex)}
                            className="text-sm text-[#00D3A8] hover:underline ml-5"
                          >
                            + Ajouter une option
                          </button>
                        </div>
                      )}

                      {question.type === 'rating' && (
                        <div className="ml-9 text-sm text-gray-500 flex items-center gap-1">
                          <Star size={14} className="text-amber-400" />
                          Notation de 1 à 5 étoiles
                        </div>
                      )}

                      {question.type === 'text' && (
                        <div className="ml-9 text-sm text-gray-500">
                          Réponse libre (texte)
                        </div>
                      )}
                    </div>
                  ))}

                  {formData.questions.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>Cliquez sur "Ajouter une question" pour commencer</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateSurvey}
                disabled={!formData.title || formData.questions.length === 0}
                className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50"
              >
                Créer le sondage
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Respond Modal */}
      {showRespondModal && selectedSurvey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-800">{selectedSurvey.title}</h2>
              {selectedSurvey.description && (
                <p className="text-sm text-gray-500 mt-1">{selectedSurvey.description}</p>
              )}
              {selectedSurvey.anonymous && (
                <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                  <CheckCircle size={12} />
                  Réponses anonymes
                </div>
              )}
            </div>

            <div className="p-6 space-y-6">
              {selectedSurvey.questions?.map((question, qIndex) => (
                <div key={question.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {qIndex + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-800">{question.text}</p>
                      {question.required && <span className="text-red-500 text-xs">* Obligatoire</span>}
                    </div>
                  </div>

                  {/* Single choice */}
                  {question.type === 'single' && (
                    <div className="ml-8 space-y-2">
                      {question.options?.map((option) => (
                        <label key={option.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white rounded-lg">
                          <input
                            type="radio"
                            name={question.id}
                            value={option.id}
                            checked={responses[question.id] === option.id}
                            onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                            className="w-4 h-4 text-[#00D3A8] focus:ring-[#00D3A8]"
                          />
                          <span className="text-gray-700">{option.text}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Multiple choice */}
                  {question.type === 'multiple' && (
                    <div className="ml-8 space-y-2">
                      {question.options?.map((option) => (
                        <label key={option.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white rounded-lg">
                          <input
                            type="checkbox"
                            value={option.id}
                            checked={(responses[question.id] || []).includes(option.id)}
                            onChange={(e) => {
                              const current = responses[question.id] || [];
                              const updated = e.target.checked
                                ? [...current, option.id]
                                : current.filter(id => id !== option.id);
                              setResponses({ ...responses, [question.id]: updated });
                            }}
                            className="w-4 h-4 text-[#00D3A8] rounded focus:ring-[#00D3A8]"
                          />
                          <span className="text-gray-700">{option.text}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Rating */}
                  {question.type === 'rating' && (
                    <div className="ml-8 flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => setResponses({ ...responses, [question.id]: rating })}
                          className={`p-2 rounded-lg transition-colors ${
                            responses[question.id] >= rating
                              ? 'text-amber-400'
                              : 'text-gray-300 hover:text-amber-200'
                          }`}
                        >
                          <Star size={28} fill={responses[question.id] >= rating ? 'currentColor' : 'none'} />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-500">
                        {responses[question.id] ? `${responses[question.id]}/5` : ''}
                      </span>
                    </div>
                  )}

                  {/* Text */}
                  {question.type === 'text' && (
                    <div className="ml-8">
                      <textarea
                        value={responses[question.id] || ''}
                        onChange={(e) => setResponses({ ...responses, [question.id]: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                        rows={3}
                        placeholder="Votre réponse..."
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => { setShowRespondModal(false); setSelectedSurvey(null); setResponses({}); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmitResponse}
                className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]"
              >
                Soumettre mes réponses
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultsModal && selectedSurvey && surveyResults && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Résultats: {selectedSurvey.title}</h2>
                <p className="text-sm text-gray-500">
                  {surveyResults.participation.total_responses} réponse{surveyResults.participation.total_responses !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => { setShowResultsModal(false); setSelectedSurvey(null); setSurveyResults(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {Object.entries(surveyResults.results).map(([qId, result], index) => (
                <div key={qId} className="bg-gray-50 rounded-lg p-5">
                  <h3 className="font-medium text-gray-800 mb-4 flex items-start gap-2">
                    <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">
                      {index + 1}
                    </span>
                    {result.question}
                  </h3>

                  {/* Single/Multiple results */}
                  {(result.type === 'single' || result.type === 'multiple') && (
                    <div className="space-y-3 ml-8">
                      {result.options?.map((option, oIndex) => (
                        <div key={oIndex}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-700">{option.text}</span>
                            <span className="text-sm font-medium text-[#00D3A8]">
                              {option.count} ({option.percentage}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-[#00D3A8] h-3 rounded-full transition-all duration-500"
                              style={{ width: `${option.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 mt-2">Total: {result.total_votes} votes</p>
                    </div>
                  )}

                  {/* Rating results */}
                  {result.type === 'rating' && (
                    <div className="ml-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              size={24}
                              className={s <= Math.round(result.average) ? 'text-amber-400' : 'text-gray-300'}
                              fill={s <= Math.round(result.average) ? 'currentColor' : 'none'}
                            />
                          ))}
                        </div>
                        <span className="text-2xl font-bold text-gray-800">{result.average}</span>
                        <span className="text-gray-500">/ 5</span>
                      </div>
                      <div className="space-y-2">
                        {result.distribution?.map((d) => (
                          <div key={d.rating} className="flex items-center gap-2 text-sm">
                            <span className="w-8">{d.rating}★</span>
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-amber-400 h-2 rounded-full"
                                style={{ width: `${result.total_votes > 0 ? (d.count / result.total_votes) * 100 : 0}%` }}
                              />
                            </div>
                            <span className="w-8 text-gray-500">{d.count}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Basé sur {result.total_votes} évaluations</p>
                    </div>
                  )}

                  {/* Text results */}
                  {result.type === 'text' && (
                    <div className="ml-8 space-y-3">
                      {result.responses?.length > 0 ? (
                        result.responses.map((r, rIndex) => (
                          <div key={rIndex} className="bg-white rounded-lg p-3 border border-gray-200">
                            <p className="text-gray-700">{r.text}</p>
                            <p className="text-xs text-gray-400 mt-1">— {r.user}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">Aucune réponse</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sondages;
